import React from 'react';
import { createRoot } from 'react-dom/client';
import { TiptapEditorComponent } from '../components/TiptapEditorComponent.jsx';

// Initialize React Tiptap editors on admin pages
class AdminTiptapInitializer {
  constructor() {
    this.editors = new Map();
    this.init();
  }

  init() {
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeEditors());
    } else {
      this.initializeEditors();
    }

    // Re-initialize on Turbo page loads (for navigation)
    document.addEventListener('turbo:load', () => this.initializeEditors());
    
    // Cleanup on page unload
    document.addEventListener('turbo:before-cache', () => this.cleanupEditors());
  }

  initializeEditors() {
    // Find all React Tiptap editor mount points
    const editorContainers = document.querySelectorAll('.react-tiptap-editor');
    
    editorContainers.forEach(container => {
      if (container.dataset.initialized === 'true') {
        return; // Already initialized
      }

      // Helper to read data attributes flexibly: supports dataset, data-underscore and data-hyphen variants
      const readData = (el, key) => {
        if (!el) return '';
        // Try dataset (camelCase and underscore versions)
        const camel = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        if (el.dataset[key] || el.dataset[camel]) {
          return el.dataset[key] || el.dataset[camel];
        }
        // Try literal attributes: data-key and data-key-with-hyphens
        return el.getAttribute(`data-${key}`) || el.getAttribute(`data-${key.replace(/_/g, '-')}`) || '';
      };

      const editorId = readData(container, 'editor_id') || 'default-editor';
      const initialContent = readData(container, 'initial_content') || '';

      // Locate the hidden input: prefer explicit data-hidden_input_id, then search nearest fieldset/parent, then fallback
      let hiddenInput = null;
      const hiddenInputId = readData(container, 'hidden_input_id');
      
      if (hiddenInputId) {
        hiddenInput = document.getElementById(hiddenInputId);
      }

      if (!hiddenInput) {
        // Search closest fieldset (ActiveAdmin pattern) or parent element for any hidden input
        const searchScope = container.closest('fieldset') || container.parentElement;
        hiddenInput = searchScope?.querySelector('input[type="hidden"]') || document.querySelector('#post_content_input');
      }

      if (!hiddenInput) {
        console.error('No hidden input found for Tiptap editor:', container, 'hiddenInputId:', hiddenInputId);
        return;
      }

      // Create React root
      const root = createRoot(container);
      
      // Determine if this is an announcement editor (simpler toolbar)
      const isAnnouncementEditor = container.closest('form')?.action?.includes('announcements') || 
                                    container.id?.includes('announcement') ||
                                    hiddenInput?.name?.includes('announcement') ||
                                    hiddenInput?.id?.includes('announcement');
      const formAction = container.closest('form')?.getAttribute('action') || '';
      const isPostEditor =
        /posts/i.test(formAction) ||
        container.id?.includes('post') ||
        hiddenInput?.name?.includes('post[') ||
        hiddenInput?.id?.includes('post_');

      const isCommentEditor =
        /comments/i.test(formAction) ||
        container.id?.includes('comment') ||
        hiddenInput?.name?.includes('comment[') ||
        hiddenInput?.id?.includes('comment_');

      const isAcademyPostEditor =
        /academy_posts/i.test(formAction) ||
        container.id?.includes('academy_post') ||
        hiddenInput?.name?.includes('academy_post[') ||
        hiddenInput?.id?.includes('academy_post_');

      // Allow explicit per-mount configuration (future entities can set these data attrs).
      const presetFromData = readData(container, 'editor_preset');
      const featuresFromData = readData(container, 'editor_features');
      let featuresOverride = {};
      if (featuresFromData) {
        try { featuresOverride = JSON.parse(featuresFromData); } catch (e) { featuresOverride = {}; }
      }

      const featuresPreset =
        presetFromData ||
        (isAnnouncementEditor ? 'announcements' : (isCommentEditor ? 'comments' : (isAcademyPostEditor ? 'academy_posts' : (isPostEditor ? 'posts' : 'posts'))));

      // Render the Tiptap editor component
      root.render(
        React.createElement(TiptapEditorComponent, {
          initialContent: initialContent,
          onChange: (content) => {
            // Update the hidden input with the new content
            hiddenInput.value = content;
            // Trigger change event for form validation
            const event = new Event('change', { bubbles: true });
            hiddenInput.dispatchEvent(event);
          },
          onReady: (editor) => {
            console.log(`Tiptap editor ${editorId} is ready`);
          },
          placeholder: isAnnouncementEditor ? 'Enter announcement description...' : 'Start writing your post content...',
          // Per-preset modifier class (e.g. `admin-tiptap-editor--posts`,
          // `admin-tiptap-editor--academy_posts`, `admin-tiptap-editor--announcements`)
          // so SCSS can target features that should ONLY apply to certain editors
          // — like the wider post editor container that fits the full toolbar in one row.
          // Without this, simpler editors (announcements, comments) inherited
          // the post-like widening and looked oversized.
          className: `admin-tiptap-editor admin-tiptap-editor--${featuresPreset}`,
          toolbarClassName: 'admin-tiptap-toolbar',
          editorClassName: 'admin-tiptap-content',
          featuresPreset,
          features: featuresOverride
        })
      );

      // Store the root for cleanup
      this.editors.set(container, root);
      container.dataset.initialized = 'true';
    });
  }

  cleanupEditors() {
    // Cleanup React roots to prevent memory leaks
    this.editors.forEach((root, container) => {
      try {
        root.unmount();
        container.dataset.initialized = 'false';
      } catch (error) {
        console.error('Error unmounting Tiptap editor:', error);
      }
    });
    
    this.editors.clear();
  }

  // Public method to manually initialize a specific editor
  initializeEditor(containerId) {
    const container = document.getElementById(containerId);
    if (container && container.classList.contains('react-tiptap-editor')) {
      container.dataset.initialized = 'false';
      this.initializeEditors();
    }
  }

  // Public method to get editor instance (for external use)
  getEditor(containerId) {
    const container = document.getElementById(containerId);
    return this.editors.get(container);
  }
}

// Export for global use
window.AdminTiptapInitializer = AdminTiptapInitializer;

// Auto-initialize
const adminTiptapInitializer = new AdminTiptapInitializer();

export default adminTiptapInitializer;
